<template>
  <div class="tabs">
    <div class="tabs__slider-wrapper">
      <span v-if="leftInd" class="tabs__left-ind"></span>
      <span v-if="rightInd" class="tabs__right-ind"></span>
      <div ref="tabsNavSlider" class="tabs__nav-slider">
        <ul ref="tabsHeader" class="tabs__header">
          <li
            class="tabs__tab-item"
            v-for="(tab, index) in tabs"
            :key="tab.title"
            @click="selectTab(index)"
            :class="{ _selected: index == selectedIndex }"
            ref="tabsNavItem"
          >
            <div
              class="tabs__tab-label"
              v-if="tabLabelType"
              v-html="getTabLabel(tab.title)"
            ></div>
            <div class="tabs__tab-label" v-else>
              <span class="tabs__tab-label-text" v-html="tab.title"></span>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div class="tabs__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "tabs",
  props: {
    tabLabelType: String,
  },
  data: () => ({
    selectedIndex: 0,
    tabs: [],
    tabsWidth: 0,
    tabsSliderWidth: 0,
    leftInd: false,
    rightInd: false,
    leftPos: 0,
  }),
  methods: {
    selectTab(i) {
      let that = this;
      this.selectedIndex = i;
      this.tabs.forEach((tab, index) => {
        tab.isActive = index === i;
        tab.selectedIndex = that.selectedIndex;
      });
    },
    getTabLabel(title) {
      return (
        "<" +
        this.tabLabelType +
        " class='tabs__tab-label-text'>" +
        title +
        "</" +
        this.tabLabelType +
        ">"
      );
    },
    mouseScroll() {
      const slider = this.$refs.tabsNavSlider;
      let that = this;
      let isDown = false;
      let startX;
      let scrollLeft;

      slider.addEventListener(
        "mousedown",
        (e) => {
          isDown = true;
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        },
        { passive: true }
      );
      slider.addEventListener(
        "mouseleave",
        () => {
          isDown = false;
        },
        { passive: true }
      );
      slider.addEventListener(
        "mouseup",
        () => {
          isDown = false;
        },
        { passive: true }
      );
      slider.addEventListener("mousemove", (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = x - startX;
        slider.scrollLeft = scrollLeft - walk;
        that.setInds();
      });
    },
    touchScroll() {
      const slider = this.$refs.tabsNavSlider;
      let that = this;
      let detectScrollEnded = function () {
        let currentScroll = slider.scrollLeft;

        function testIfScrolling() {
          if (slider.scrollLeft !== currentScroll) {
            window.requestAnimationFrame(testIfScrolling);
            that.setInds();
          } else {
            that.setInds();
          }
        }
        window.requestAnimationFrame(testIfScrolling);
      };
      slider.addEventListener("touchend", detectScrollEnded, { passive: true });
      slider.addEventListener(
        "touchmove",
        () => {
          that.setInds();
        },
        { passive: true }
      );
    },
    calculateTabSliderWidth() {
      this.tabsSliderWidth = this.$refs.tabsNavSlider.clientWidth;
    },
    getNavWidth() {
      const item = this.$refs.tabsNavItem;
      let itemsWidthSum = 0;

      for (let i = 0; i < this.tabs.length; i++) {
        itemsWidthSum += parseInt(item[i].clientWidth);
      }
      this.tabsWidth = itemsWidthSum;
    },
    setInds() {
      const slider = this.$refs.tabsNavSlider;
      this.leftPos = slider.scrollLeft;

      if (
        this.tabsSliderWidth < this.tabsWidth &&
        this.leftPos < this.tabsWidth - this.tabsSliderWidth - 12
      ) {
        this.rightInd = true;
      } else {
        this.rightInd = false;
      }
      if (this.leftPos > 12) {
        this.leftInd = true;
      } else {
        this.leftInd = false;
      }
    },
  },
  created() {
    this.tabs = this.$children;
    window.addEventListener("resize", this.calculateTabSliderWidth, {
      passive: true,
    });
  },
  destroyed() {
    window.removeEventListener("resize", this.calculateTabSliderWidth, {
      passive: true,
    });
  },
  updated() {
    this.getNavWidth();
    this.setInds();
  },
  mounted() {
    this.selectTab(0);
    this.$nextTick(() => {
      this.calculateTabSliderWidth();
      this.mouseScroll();
      this.touchScroll();
    });
  },
  watch: {
    tabs: function () {
      this.selectTab(0);
    },
  },
};
</script>

<style lang="scss" scoped>
.tabs {
  &__slider-wrapper {
    border-radius: 3px;
    margin-bottom: 32px;
    overflow: hidden;
    position: relative;
  }

  &__left-ind,
  &__right-ind {
    background-color: $blue-dark;
    position: absolute;
    top: 0;
    height: 100%;
    width: 16px;
    z-index: 1;
  }

  &__left-ind {
    left: 0;
    background: linear-gradient(
      90deg,
      rgba($blue-dark, 1) 20%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  &__right-ind {
    right: 0;
    background: linear-gradient(
      -90deg,
      rgba($blue-dark, 1) 20%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  &__nav-slider {
    background-color: $blue-dark;
    overflow-x: auto;
    overflow: -moz-scrollbars-none;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__header {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    position: relative;
  }

  &__tab-item {
    cursor: pointer;
    color: #fff;
    display: inline-block;
    padding: 12px 18px;
    position: relative;
    white-space: nowrap;
    user-select: none;
    transition: background-color ease-in-out 0.1s;

    &::before,
    &::after {
      content: "";
      display: block;
      width: 1px;
      height: 100%;
      position: absolute;
      top: 0;
    }

    &:not(&._selected) {
      &:hover,
      &:active {
        background-color: lighten($blue-dark, 5%);
      }

      &::after {
        background-color: #3f6b95;
        right: 0;
      }
    }

    &._selected {
      background: $blue-light;
      cursor: default;

      &::before {
        background-color: $blue-light;
        left: -1px;
      }
    }
  }
}

::v-deep .tabs {
  &__tab-label-text {
    @include font-r();
    font-size: 17px;
    margin-bottom: 0;
  }
}
</style>
